import type { DeviceSource } from "./types"

/** The parts of a source the labels need; generated GraphQL types fit too. */
type LabelledSource = Pick<DeviceSource, "ref" | "label" | "deviceType"> & { description?: string | null }

/**
 * What each Device Source type is, for telling apart sources that share a label.
 *
 * One VRM block can feed several sources: "System overview [0]" gives a
 * Generator, a DC Load and an AC Load slot, all with that label. The server's
 * `description` (from core's Device Source Catalogue) wins; this map is the
 * fallback for a mapped source discovery no longer lists, which has none.
 */
const DEVICE_TYPE_NAMES: Record<string, string> = {
  genset: "Generator",
  dc: "DC Load",
  ac: "AC Load",
  charger: "VE.Bus",
  inverter: "Inverter",
  pv_inverter: "PV Inverter",
  solar: "Solar Charger",
  battery: "Battery",
  tank: "Tank",
}

/**
 * Fallback names for a device type's variants, keyed "deviceType:variant".
 * Used only when the server sent no description (a mapped source discovery
 * no longer lists). An unknown variant falls back to the base type name.
 */
const VARIANT_NAMES: Record<string, string> = {
  "genset:vebus": "Generator (VE.Bus, 3-phase data)",
}
/** Split a slot ref "deviceType:instance[:variant]" into its parts. */
function parseRef(ref: string): { deviceType: string; instance: string | null; variant: string | null } {
  const [deviceType, instance = null, variant = null] = ref.split(":", 3)
  return { deviceType, instance, variant: variant || null }
}
function nameFor(deviceType: string, description: string | null | undefined): string | null {
  return description || DEVICE_TYPE_NAMES[deviceType] || null
}

function labelIsShared(label: string, ref: string, sources: LabelledSource[]): boolean {
  return sources.some((s) => s.label === label && s.ref !== ref)
}

/** A source's label, with its name appended when another source shares the label. */
export function sourceDisplayLabel(source: LabelledSource, sources: LabelledSource[]): string {
  const name = nameFor(source.deviceType, source.description)
  return name && labelIsShared(source.label, source.ref, sources) ? `${source.label} · ${name}` : source.label
}

/**
 * The name to show beside a mapped source, or null when its label is unique.
 * Works from the mapping's ref, so it still names a source discovery no longer lists.
 */
export function mappedSourceLabel(sourceRef: string, sourceLabel: string, sources: LabelledSource[]): string | null {
  const known = sources.find((s) => s.ref === sourceRef)
  const { deviceType, variant } = parseRef(sourceRef)
  const variantName = variant ? VARIANT_NAMES[`${deviceType}:${variant}`] : undefined
  const name = known?.description || variantName || nameFor(known?.deviceType ?? deviceType, null)
  return name && labelIsShared(sourceLabel, sourceRef, sources) ? name : null
}
